import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { TEXT_KEY, getText } from 'shared/utils/localization';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import { TYPEOF } from 'shared/constants';
import Button from '../../../../../components/Button/Button';
import { isNotStandaloneMobilePWA, isStandalonePWA } from '../../../../../utils/mobileSupported';
import { isAndroid, isFirefox, isIOS, isTablet } from 'react-device-detect';
import ScalableText from '../../../../../components/ScalableText/ScalableText';

const InviteScreen = props => {
  const { friendsInviteFeatureState, friendsInviteAssets } = props;

  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [showInviteLink, setShowInviteLink] = useState(false);

  const copyInviteLink = async () => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === TYPEOF.FUNCTION) {
        await navigator.clipboard.writeText(friendsInviteFeatureState?.inviteLink?.inviteLinkUrl);
        setIsLinkCopied(true);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = friendsInviteFeatureState?.inviteLink?.inviteLinkUrl;
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        textarea.style.zIndex = '-1';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setIsLinkCopied(true);
      }
    } catch (err) {}
  };

  return (
    <Container {...props}>
      <TitleImage src={friendsInviteAssets?.SOCIAL_FRIENDS_MORE_COINS_TITLE} showInviteLink={showInviteLink} />
      <Body>
        <Description isInviteLink={showInviteLink}>
          {!showInviteLink
            ? getText(TEXT_KEY.SOCIAL_INVITE_SCREEN_DESCRIPTION)
            : getText(TEXT_KEY.SOCIAL_INVITE_SCREEN_DESCRIPTION_WEB)}
        </Description>
        {!showInviteLink ? (
          <Button
            onClick={() => setShowInviteLink(true)}
            imageSource={assetSource(ASSET_KEY.ROUNDED_RECT_PRIMARY)}
            label={getText(TEXT_KEY.SOCIAL_INVITE_SCREEN_BUTTON)}
            containerStyle={ButtonStyle}
          />
        ) : (
          <ScalableText
            wrapperStyle={InviteLinkWrapperStyle}
            textStyle={InviteLinkTextStyle}
            text={friendsInviteFeatureState?.inviteLink?.inviteLinkUrl}
            onClick={copyInviteLink}
          />
        )}
        {isLinkCopied && <CopiedLink>{getText(TEXT_KEY.SOCIAL_INVITE_SCREEN_LINK_COPIED)}</CopiedLink>}
        <Footer>{getText(TEXT_KEY.SOCIAL_INVITE_SCREEN_FOOTER)}</Footer>
      </Body>
      <BottomImage src={friendsInviteAssets?.SOCIAL_FRIENDS_CHARACTERS_IMAGE} showInviteLink={showInviteLink} />
    </Container>
  );
};

export default InviteScreen;

const Container = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  opacity: ${({ isGuestUser, hasActiveState }) => (isGuestUser || !hasActiveState ? 0.1 : 1)};
  display: ${({ limitReached, friendsInviteAssets }) => (limitReached || !friendsInviteAssets ? 'none' : 'flex')};
  ${({ theme }) =>
    theme.screens.device('mobile-browser') &&
    theme.platform('android') &&
    `
     height: ${window.innerHeight < 700 ? 98 : 100}%;
    `}
`;

const TitleImage = styled.img`
  width: 100%;
  height: ${isFirefox ? '9rem' : '10rem'};
  object-fit: contain;
  position: relative;
  top: 1rem;

  ${({ theme }) => theme.breakpoints.only('lg')} {
    top: ${({ showInviteLink }) => (showInviteLink ? 0.5 : 1.55)}rem;
  }
  ${({ theme }) => theme.breakpoints.only('md')} {
    top: ${({ showInviteLink }) => (showInviteLink ? 0 : 2.25)}rem;
  }
  ${({ theme }) => theme.breakpoints.only('sm')} {
    top: ${({ showInviteLink }) => (showInviteLink ? 0.85 : 1)}rem;
  }
`;

const Body = styled.div`
  width: 100%;
  height: 55%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  padding-left: 1.8rem;
  padding-right: 1.8rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-bottom: 2%;
  }

  ${({ theme }) =>
    theme.platform('android') &&
    !theme.screens.device('tablet-browser') &&
    `
     margin-top: -4rem;
     height: 60%;
    `}
`;

const Description = styled.p`
  font-size: 1.2rem;
  color: white;
  text-align: center;
  margin-top: ${({ isInviteLink }) => (isIOS && isFirefox ? '0px' : (isInviteLink && isNotStandaloneMobilePWA) || (isInviteLink && isAndroid && isStandalonePWA) ? '2rem' : '0px')};
  font-weight: 700;
  width: ${({ isInviteLink }) => (isInviteLink ? '100%' : isTablet ? '70%' : '45%')};
  ${({ theme }) => isFirefox && theme.screens.devices(['mobile-browser', 'mobile-standalone']) && 'margin-bottom: 0px;'}
`;

const InviteLinkTextStyle = css`
  font-size: 1.2rem;
  color: white;
  text-align: center;
  font-weight: 700;
  cursor: pointer;
  z-index: 10;
`;

const InviteLinkWrapperStyle = css`
  width: 95%;
`;

const CopiedLink = styled.p`
  font-size: 0.9rem;
  color: white;
  text-align: center;
  position: absolute;
  top: 25rem;

  ${({ theme }) => theme.breakpoints.only('md')} {
    top: 23.5rem;
  }

  ${({ theme }) => theme.screens.device('mobile-browser') && 'top: 49vh;'}
  ${({ theme }) => theme.screens.device('mobile-standalone') && 'top: 47vh;'}
  ${({ theme }) => theme.screens.device('tablet-browser') && 'top: 46vh;'}
  ${({ theme }) => theme.platform('android') && theme.screens.device('mobile-browser') && 'top: 48vh;'}
  ${({ theme }) => theme.platform('android') && theme.screens.device('mobile-standalone') && 'top: 46vh;'}
`;

const Footer = styled.p`
  font-size: 0.8rem;
  color: white;
  text-align: center;
`;

const BottomImage = styled.img`
  height: 20vh;
  width: 40rem;
  ${!isTablet && `height: ${({ showInviteLink }) => (showInviteLink ? 20 : 25)}rem`};
  object-fit: contain;
  position: relative;
  top: 1vh;

  ${({ theme }) => theme.breakpoints.only('lg')} {
    top: ${({ showInviteLink }) => (showInviteLink ? 1 : -1)}vh;
  }

  ${({ theme }) => theme.breakpoints.only('md')} {
    height: 17vh;
    top: ${({ showInviteLink }) => (showInviteLink ? 1 : -3.65)}vh;
  }

  ${({ theme }) => theme.breakpoints.only('sm')} {
    top: ${({ showInviteLink }) => (showInviteLink ? 1 : 0.8)}vh;
  }

  ${({ theme }) => theme.screens.device('mobile-browser') && theme.platform('android') && 'top: 0.8vh;'}
  ${({ theme, showInviteLink }) =>
    theme.screens.device('mobile-standalone') &&
    theme.platform('android') &&
    `top: ${window.innerHeight < 800 ? (showInviteLink ? 0.4 : 0) : 0.7}vh;`}
   ${({ theme }) => theme.screens.device('tablet-browser') && theme.platform('android') && 'top: 0.6vh;'}
`;

const ButtonStyle = {
  marginTop: '1rem',
  marginBottom: isNotStandaloneMobilePWA ? '1.5rem' : '2rem',
};

